import { Link } from 'react-router-dom'
import type { AnalyticsSummary } from '../../../shared/api/analytics.api'
import { convClass, fmtInt, fmtRub, moneyConvPct } from './summaryFormat'
import { useAnalyticsTable, type ColumnDef } from '../shared/useAnalyticsTable'
import { SortableTh } from '../shared/SortableTh'

type ClientRow = AnalyticsSummary['clients'][number]
type ManagerRow = AnalyticsSummary['managers'][number]

/** Ячейка денежной конверсии «В заказ / КП»: `—` когда КП = 0. */
function MoneyConvCell({ ordered, kp }: { ordered: number; kp: number }) {
  const mc = moneyConvPct(ordered, kp)
  if (mc === null) {
    return <td className="py-2 pl-3 text-right text-gray-400 tabular-nums">—</td>
  }
  return <td className={`py-2 pl-3 text-right font-medium tabular-nums ${convClass(mc)}`}>{mc}%</td>
}

const CLIENT_COLUMNS: ColumnDef<ClientRow>[] = [
  { key: 'name', sort: (r) => r.name.toLowerCase(), filter: (r) => r.name },
  { key: 'segment', sort: (r) => r.segment, filter: (r) => r.segment },
  { key: 'category', sort: (r) => r.category, filter: (r) => r.category },
  { key: 'requests', sort: (r) => r.requests },
  { key: 'kpSum', sort: (r) => r.kpSum },
  { key: 'orderedSum', sort: (r) => r.orderedSum },
  { key: 'conversion', sort: (r) => r.conversion },
  { key: 'moneyConv', sort: (r) => moneyConvPct(r.orderedSum, r.kpSum) ?? -1 },
]

export function SummaryClientsTable({ data }: { data: AnalyticsSummary }) {
  const table = useAnalyticsTable(data.clients, CLIENT_COLUMNS, undefined, 'summary-clients')
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-gray-900">Клиенты периода</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Топ-20 по числу заявок. Бейдж «нов.» — первая заявка клиента в этом периоде. «Конв. по
        заявкам» — доля заявок клиента, ушедших в заказ (шт); «В заказ / КП» — доля денег КП,
        реализованных в заказ.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <SortableTh table={table} col="name" className="pr-3">
                Клиент
              </SortableTh>
              <SortableTh table={table} col="segment" className="px-3">
                Сегмент
              </SortableTh>
              <SortableTh table={table} col="category" className="px-3">
                Кат.
              </SortableTh>
              <SortableTh table={table} col="requests" align="right" className="px-3">
                Заявок
              </SortableTh>
              <SortableTh table={table} col="kpSum" align="right" className="px-3">
                Сумма КП
              </SortableTh>
              <SortableTh table={table} col="orderedSum" align="right" className="px-3">
                В заказ
              </SortableTh>
              <SortableTh table={table} col="conversion" align="right" className="px-3">
                Конв. по заявкам, %
              </SortableTh>
              <SortableTh table={table} col="moneyConv" align="right" className="pl-3">
                В заказ / КП, %
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.view.map((c) => (
              <tr key={c.clientId} className="border-b border-gray-50">
                <td className="py-2 pr-3">
                  <Link
                    to={`/clients/${c.clientId}`}
                    className="text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {c.name}
                  </Link>
                  {c.isNew && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px]">
                      нов.
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-gray-600">{c.segment}</td>
                <td className="py-2 px-3 text-gray-600">{c.category}</td>
                <td className="py-2 px-3 text-right text-gray-800 tabular-nums">
                  {fmtInt(c.requests)}
                </td>
                <td className="py-2 px-3 text-right text-gray-600 tabular-nums">
                  {fmtRub(c.kpSum)}
                </td>
                <td className="py-2 px-3 text-right text-gray-800 tabular-nums">
                  {fmtRub(c.orderedSum)}
                </td>
                <td
                  className={`py-2 px-3 text-right font-medium tabular-nums ${convClass(c.conversion)}`}
                >
                  {c.conversion}%
                </td>
                <MoneyConvCell ordered={c.orderedSum} kp={c.kpSum} />
              </tr>
            ))}
            {table.view.length === 0 && (
              <tr>
                <td colSpan={8} className="py-6 text-center text-sm text-gray-400">
                  Нет заявок за период
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const MANAGER_COLUMNS: ColumnDef<ManagerRow>[] = [
  { key: 'name', sort: (r) => r.name.toLowerCase(), filter: (r) => r.name },
  { key: 'requests', sort: (r) => r.requests },
  { key: 'won', sort: (r) => r.won },
  { key: 'kpSum', sort: (r) => r.kpSum },
  { key: 'orderedSum', sort: (r) => r.orderedSum },
  { key: 'conversion', sort: (r) => r.conversion },
  { key: 'moneyConv', sort: (r) => moneyConvPct(r.orderedSum, r.kpSum) ?? -1 },
]

export function SummaryManagersTable({ data }: { data: AnalyticsSummary }) {
  const table = useAnalyticsTable(data.managers, MANAGER_COLUMNS, undefined, 'summary-managers')
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-gray-900">Срез по менеджерам</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Заявки/КП/заказ за период. «Конв. по заявкам» — доля заявок в заказ (шт); «В заказ / КП» —
        доля денег КП в заказ. Полная аналитика — на вкладке «Менеджеры».
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <SortableTh table={table} col="name" className="pr-3">
                Менеджер
              </SortableTh>
              <SortableTh table={table} col="requests" align="right" className="px-3">
                Заявок
              </SortableTh>
              <SortableTh table={table} col="won" align="right" className="px-3">
                В заказ (шт)
              </SortableTh>
              <SortableTh table={table} col="kpSum" align="right" className="px-3">
                Сумма КП
              </SortableTh>
              <SortableTh table={table} col="orderedSum" align="right" className="px-3">
                Сумма в заказ
              </SortableTh>
              <SortableTh table={table} col="conversion" align="right" className="px-3">
                Конв. по заявкам, %
              </SortableTh>
              <SortableTh table={table} col="moneyConv" align="right" className="pl-3">
                В заказ / КП, %
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.view.map((m) => (
              <tr key={m.managerId} className="border-b border-gray-50">
                <td className="py-2 pr-3 text-gray-800 font-medium">{m.name}</td>
                <td className="py-2 px-3 text-right text-gray-800 tabular-nums">
                  {fmtInt(m.requests)}
                </td>
                <td className="py-2 px-3 text-right text-gray-600 tabular-nums">{fmtInt(m.won)}</td>
                <td className="py-2 px-3 text-right text-gray-600 tabular-nums">
                  {fmtRub(m.kpSum)}
                </td>
                <td className="py-2 px-3 text-right text-gray-800 tabular-nums">
                  {fmtRub(m.orderedSum)}
                </td>
                <td
                  className={`py-2 px-3 text-right font-medium tabular-nums ${convClass(m.conversion)}`}
                >
                  {m.conversion}%
                </td>
                <MoneyConvCell ordered={m.orderedSum} kp={m.kpSum} />
              </tr>
            ))}
            {table.view.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-sm text-gray-400">
                  Нет данных за период
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
